import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import PerformanceResultCard from './PerformanceResultCard';
import { mapResultCards } from '../../NewStatsTabContent/PerformanceStats/performanceStatsUtils';
import { trackEvent } from '../../../Analytics/TrackedClick';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../../Analytics/constants';
import useStartPowerCheckout from '../../../hooks/useStartPowerCheckout';
import { performanceStatsAnalyticsEvents, getActivityStatsTraits } from '../../../../utils/analytics';
import { PlayerProfileTabIdentifiers } from '../../../../utils/constants';

import styles from './performanceStatsResults.scss';

const OVERVIEW_RESULTS_COUNT = 2;
const PERFORMANCE_STATS_VIEW = 'performance';

function PerformanceStatsResults({ playerId, data, auth, isOwnProfile, ctaCard, 'data-testid': dataTestId }) {
    const { startPowerCheckout } = useStartPowerCheckout();

    const isLocked = !auth?.user?.isPowered || data?.isPowerUser === false;
    const cards = useMemo(() => mapResultCards(data?.results, playerId, OVERVIEW_RESULTS_COUNT), [data, playerId]);

    const launchPaywall = useCallback(() => {
        trackEvent(performanceStatsAnalyticsEvents.GET_POWER_CTA_TAPPED, getActivityStatsTraits(auth, playerId, isOwnProfile));
        startPowerCheckout({
            surface: PowerFunnelSurfaces.PLAYER_PROFILE,
            gatedFeature: PowerFunnelGatedFeatures.PERFORMANCE_STATS,
        });
    }, [startPowerCheckout, auth, playerId, isOwnProfile]);

    const statsTabHref = `/profiles/${playerId}?t=${PlayerProfileTabIdentifiers.STATS}&view=${PERFORMANCE_STATS_VIEW}`;

    const handleViewAllClick = () => {
        trackEvent(performanceStatsAnalyticsEvents.VIEW_ALL_RESULTS_TAPPED, getActivityStatsTraits(auth, playerId, isOwnProfile));
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    if (!cards.length) { return null; }

    return (
        <>
            <div className={styles.headerRow} data-testid={dataTestId}>
                <h6 className={`title`} data-testid={dataTestId && `${dataTestId}.title`}>Recent Results</h6>
                <Link
                    className={styles.viewAllLink}
                    to={statsTabHref}
                    onClick={handleViewAllClick}
                    data-testid={dataTestId && `${dataTestId}.view-all-link`}
                >
                    View All Performance Stats
                </Link>
            </div>
            <div className="row">
                {cards.map((card, cardIndex) => (
                    <div className="col-12 col-md-6" key={card.id ?? cardIndex}>
                        <PerformanceResultCard
                            card={card}
                            locked={isLocked && cardIndex > 0}
                            onLockClick={launchPaywall}
                            data-testid={dataTestId && `${dataTestId}.card-${card.id ?? cardIndex}`}
                        />
                    </div>
                ))}
                {ctaCard}
            </div>
        </>
    );
}

PerformanceStatsResults.propTypes = {
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.shape({
        isPowerUser: PropTypes.bool,
        results: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    auth: PropTypes.shape({
        user: PropTypes.shape({
            isPowered: PropTypes.bool,
        }),
    }),
    isOwnProfile: PropTypes.bool,
    ctaCard: PropTypes.node,
    'data-testid': PropTypes.string,
};

PerformanceStatsResults.defaultProps = {
    playerId: null,
    data: null,
    auth: null,
    isOwnProfile: false,
    ctaCard: null,
    'data-testid': null,
};

export default PerformanceStatsResults;
